import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  TextField,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Add as AddIcon,
  VideoCall as VideoCallIcon,
} from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import apiInstance from "src/utils/apiAuth";
import * as constants from "src/utils/constants";
import axios from "axios";
import { showErrorToast, showToast } from "src/utils/Toast/SnackbarProvider";
import ct from "countries-and-timezones";

// Get all timezones using countries-and-timezones library
const getAllTimezones = () => {
  try {
    const allTimezones = ct.getAllTimezones();
    const allCountries = ct.getAllCountries();

    // Convert to array and enrich with country information
    const timezoneList = Object.keys(allTimezones).map((tzName) => {
      const tz = allTimezones[tzName];

      // Find countries using this timezone
      const countriesForTz = tz.countries || [];
      const countryNames = countriesForTz
        .map((countryCode) => {
          const country = allCountries[countryCode];
          return country ? country.name : "";
        })
        .filter((name) => name)
        .join(", ");

      // Format UTC offset
      const offsetMinutes = tz.utcOffset;
      const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
      const offsetMins = Math.abs(offsetMinutes) % 60;
      const sign = offsetMinutes >= 0 ? "+" : "-";
      const utcLabel =
        offsetMins > 0
          ? `UTC${sign}${offsetHours}:${offsetMins.toString().padStart(2, "0")}`
          : `UTC${sign}${offsetHours}`;

      // Create search text with timezone name, country names, and cities
      const searchTerms = [
        tzName.toLowerCase().replace(/_/g, " "),
        countryNames.toLowerCase(),
        utcLabel.toLowerCase(),
        tz.aliasOf ? tz.aliasOf.toLowerCase() : "",
      ]
        .filter((term) => term)
        .join(" ");

      return {
        value: tzName,
        label: `${tzName.replace(/_/g, " ")} ${countryNames ? `(${countryNames})` : ""} - ${utcLabel}`,
        countries: countryNames,
        offset: offsetMinutes,
        searchText: searchTerms,
      };
    });

    // Sort by UTC offset, then by name
    return timezoneList.sort((a, b) => {
      if (a.offset !== b.offset) {
        return a.offset - b.offset;
      }
      return a.value.localeCompare(b.value);
    });
  } catch (error) {
    console.error("Error getting timezones from library:", error);
    // Fallback to minimal list
    return [
      {
        value: "Asia/Kolkata",
        label: "Asia/Kolkata (India) - UTC+5:30",
        countries: "India",
        offset: 330,
        searchText: "asia kolkata india utc+5:30",
      },
    ];
  }
};

function getMinStartDate() {
  const currentDate = new Date();
  const year = currentDate.getFullYear();
  const month = String(currentDate.getMonth() + 1).padStart(2, "0");
  const day = String(currentDate.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export default function MeetingScheduler({
  onClose,
  companyId,
  companyName,
  stages,
  currentStageId,
  currentStageName,
  companyCountry,
  companyTeamAssignments = [],
  userRoles = [],
  onSuccess,
}) {
  const [journeyItemData, setJourneyItemData] = useState([]);
  const [userData, setUserData] = useState([]);
  const [companyMembersData, setCompanyMembersData] = useState([]);
  const [showInterviewDetails, setShowInterviewDetails] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedTimezone, setSelectedTimezone] = useState("Asia/Kolkata");
  const [userSelectedTimezone, setUserSelectedTimezone] = useState(false); // Track if user manually selected
  const [initialLoading, setInitialLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [meetingType, setMeetingType] = useState("microsoft"); // 'microsoft' or 'gateway'
  const [outlookConnected, setOutlookConnected] = useState(false);
  const [outlookExpired, setOutlookExpired] = useState(false);
  const [outlookExpiredEmails, setOutlookExpiredEmails] = useState([]);
  const [checkingOutlook, setCheckingOutlook] = useState(false);
  const [meetingTitle, setMeetingTitle] = useState("");
  const [meetingDescription, setMeetingDescription] = useState("");
  const [outlookConnections, setOutlookConnections] = useState([]);
  const [selectedConnectionId, setSelectedConnectionId] = useState(null);

  // Get all timezones dynamically
  const allTimezones = useMemo(() => getAllTimezones(), []);

  // Calculate time difference between selected timezone and user's local timezone
  const getTimeDifference = (timezone) => {
    try {
      const now = new Date();
      const userOffset = -now.getTimezoneOffset() / 60; // User's local timezone offset in hours

      // Get selected timezone offset
      const utcDate = new Date(
        now.toLocaleString("en-US", { timeZone: "UTC" }),
      );
      const tzDate = new Date(
        now.toLocaleString("en-US", { timeZone: timezone }),
      );
      const tzOffset = (tzDate - utcDate) / (1000 * 60 * 60);

      const difference = tzOffset - userOffset;

      if (difference === 0) return "Same as your timezone";

      const absDiff = Math.abs(difference);
      const hours = Math.floor(absDiff);
      const minutes = Math.round((absDiff - hours) * 60);

      const timeStr = minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
      return difference > 0 ? `${timeStr} ahead` : `${timeStr} behind`;
    } catch (error) {
      return "";
    }
  };

  // Auto-set timezone when company country is provided (only if user hasn't manually selected)
  useEffect(() => {
    if (
      companyCountry &&
      allTimezones &&
      allTimezones.length > 0 &&
      !userSelectedTimezone
    ) {
      try {
        // Use the library to find timezone by country name
        const allCountries = ct.getAllCountries();

        // Find country by name
        const countryEntry = Object.values(allCountries).find(
          (country) =>
            country.name.toLowerCase() === companyCountry.toLowerCase(),
        );

        if (
          countryEntry &&
          countryEntry.timezones &&
          countryEntry.timezones.length > 0
        ) {
          // Get the first (main) timezone for the country
          const mainTimezone = countryEntry.timezones[0];
          setSelectedTimezone(mainTimezone);
        } else {
          // Fallback: search in our timezone list by country name
          const matchingTimezone = allTimezones.find(
            (tz) =>
              tz.countries &&
              tz.countries.toLowerCase().includes(companyCountry.toLowerCase()),
          );

          if (matchingTimezone) {
            setSelectedTimezone(matchingTimezone.value);
          }
        }
      } catch (error) {
        console.error("Error setting timezone from country:", error);
      }
    }
  }, [companyCountry, allTimezones]);

  // Auto-select SAM and PM as moderators when userData loads
  useEffect(() => {
    if (
      userData.length > 0 &&
      companyTeamAssignments.length > 0 &&
      userRoles.length > 0
    ) {
      // Find SAM and PM assignments
      const samAssignment = companyTeamAssignments.find((assignment) => {
        const role = userRoles.find(
          (r) => r.id?.toString() === assignment.role?.toString(),
        );
        // Match on stable role name (SAM), not the display label (now "BM").
        return role?.name?.toLowerCase() === "sam";
      });

      const pmAssignment = companyTeamAssignments.find((assignment) => {
        const role = userRoles.find(
          (r) => r.id?.toString() === assignment.role?.toString(),
        );
        // Match on stable role name (PM), not the display label (now "BA").
        return role?.name?.toLowerCase() === "pm";
      });

      const autoSelectedUsers = [];

      if (samAssignment && samAssignment.user) {
        autoSelectedUsers.push(samAssignment.user);
      }

      if (pmAssignment && pmAssignment.user) {
        autoSelectedUsers.push(pmAssignment.user);
      }

      // Set the auto-selected users
      if (autoSelectedUsers.length > 0) {
        formik.setFieldValue("users", autoSelectedUsers);
      }
    }
  }, [userData, companyTeamAssignments, userRoles]);

  // Auto-select all company members when companyMembersData loads
  useEffect(() => {
    if (companyMembersData.length > 0) {
      // Auto-select all company members
      const allCompanyMembers = companyMembersData.map((member) => member.id);
      formik.setFieldValue("company_members", allCompanyMembers);
    }
  }, [companyMembersData]);

  // Check Outlook connection status
  const checkOutlookConnection = async () => {
    setCheckingOutlook(true);
    try {
      const response = await axios.get(
        `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/outlook/auth/status/`,
        {
          headers: {
            Authorization: `Token ${localStorage.getItem("authToken")}`,
          },
        },
      );
      const connections = response.data?.connections || [];
      const isConnected =
        response.data?.connected === true || connections.length > 0;

      // Check if any connection has an expired token
      const expiredConns = connections.filter((c) => c.token_expired);
      const hasExpired = expiredConns.length > 0;
      const allExpired =
        isConnected &&
        connections.length > 0 &&
        expiredConns.length === connections.length;

      setOutlookConnected(isConnected && !allExpired);
      setOutlookExpired(hasExpired);
      setOutlookExpiredEmails(expiredConns.map((c) => c.email));

      // Store connections list and auto-select default (only non-expired)
      setOutlookConnections(connections);
      const defaultConn = response.data?.default_connection;
      if (defaultConn && !defaultConn.token_expired) {
        setSelectedConnectionId(String(defaultConn.id));
      } else {
        // Find first non-expired connection
        const validConn = connections.find((c) => !c.token_expired);
        if (validConn) {
          setSelectedConnectionId(String(validConn.id));
        } else if (connections.length > 0) {
          setSelectedConnectionId(String(connections[0].id));
        }
      }
    } catch (error) {
      console.error("Failed to check Outlook connection:", error);
      setOutlookConnected(false);
      setOutlookExpired(false);
      setOutlookExpiredEmails([]);
      setOutlookConnections([]);
    } finally {
      setCheckingOutlook(false);
    }
  };

  // Create Microsoft Teams meeting via Outlook Calendar API
  const createMicrosoftMeeting = async (meetingData) => {
    // Include connection_id so the correct account sends the invite
    if (selectedConnectionId) {
      meetingData.connection_id = selectedConnectionId;
    }
    const response = await axios.post(
      `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/outlook/events/`,
      meetingData,
      {
        headers: {
          Authorization: `Token ${localStorage.getItem("authToken")}`,
        },
      },
    );
    return response.data;
  };

  // Check Outlook connection on mount (independent of companyId)
  useEffect(() => {
    checkOutlookConnection();
  }, []);

  useEffect(() => {
    const loadData = async () => {
      if (companyId) {
        try {
          setInitialLoading(true);
          setHasError(false);
          // Load all data - individual errors are handled within each function
          await Promise.allSettled([
            getJourneyItemData(),
            getUserData(),
            getCompanyMembers(),
          ]);
        } catch (error) {
          console.error("Error loading meeting scheduler data:", error);
          setHasError(true);
        } finally {
          setInitialLoading(false);
        }
      } else {
        setInitialLoading(false);
      }
    };

    loadData();
  }, [companyId]);

  const getJourneyItemData = async () => {
    try {
      const response = await apiInstance.get(
        `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/journey-item?`,
        {
          headers: {
            Authorization: `Token ${localStorage.getItem("authToken")}`,
          },
        },
      );
      setJourneyItemData(response?.data?.data || []);
    } catch (error) {
      showErrorToast("Error loading journey items");
    }
  };

  const getUserData = async () => {
    try {
      const response = await apiInstance.get(
        `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/users-viewset?short_details=true`,
        {
          headers: {
            Authorization: `Token ${localStorage.getItem("authToken")}`,
          },
        },
      );
      setUserData(response?.data?.data?.filter((obj) => obj?.is_active) || []);
    } catch (error) {
      showErrorToast("Error loading users");
    }
  };

  const getCompanyMembers = async () => {
    try {
      const response = await apiInstance.get(`company/${companyId}/`, {
        headers: {
          Authorization: `Token ${localStorage.getItem("authToken")}`,
        },
      });
      const companyData = response?.data?.data;
      setCompanyMembersData(companyData?.company_members || []);
    } catch (error) {
      showErrorToast("Error loading company members");
    }
  };

  const [additionalEmails, setAdditionalEmails] = useState([]);
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState("");
  const [customHours, setCustomHours] = useState("");
  const [ccEmails, setCcEmails] = useState([]);
  const [ccInput, setCcInput] = useState("");
  const [ccError, setCcError] = useState("");

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleAddEmail = () => {
    const trimmedEmail = emailInput.trim();
    if (!trimmedEmail) {
      setEmailError("Please enter an email address");
      return;
    }
    if (!validateEmail(trimmedEmail)) {
      setEmailError("Please enter a valid email address");
      return;
    }
    if (additionalEmails.includes(trimmedEmail)) {
      setEmailError("Email already added");
      return;
    }
    setAdditionalEmails([...additionalEmails, trimmedEmail]);
    setEmailInput("");
    setEmailError("");
  };

  const handleDeleteEmail = (emailToDelete) => {
    setAdditionalEmails(
      additionalEmails.filter((email) => email !== emailToDelete),
    );
  };

  const handleAddCcEmail = () => {
    const trimmedEmail = ccInput.trim();
    if (!trimmedEmail) {
      setCcError("Please enter an email address");
      return;
    }
    if (!validateEmail(trimmedEmail)) {
      setCcError("Please enter a valid email address");
      return;
    }
    if (ccEmails.includes(trimmedEmail)) {
      setCcError("Email already added");
      return;
    }
    setCcEmails([...ccEmails, trimmedEmail]);
    setCcInput("");
    setCcError("");
  };

  const handleDeleteCcEmail = (emailToDelete) => {
    setCcEmails(ccEmails.filter((email) => email !== emailToDelete));
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const validationSchema = Yup.object().shape({
    company_members: Yup.array().min(
      1,
      "At least one company member is required",
    ),
    users: Yup.array().min(1, "At least one moderator is required"),
    date: Yup.string().required("Date is required"),
    duration: Yup.number()
      .typeError("Duration must be a number")
      .required("Duration is required"),
  });

  const formik = useFormik({
    initialValues: {
      stage: currentStageId || "",
      company_members: [],
      users: [],
      date: "",
      time: "09:00",
      duration: 60,
      timezone: "Asia/Kolkata",
      showInterviewDetails: false,
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        // Format datetime strings (timezone is sent separately)
        const startDateISO = `${values.date}T${values.time}:00`;

        // Calculate end time by adding duration (pure math, no timezone conversion)
        const [startHours, startMinutes] = values.time.split(":").map(Number);
        const [year, month, day] = values.date.split("-").map(Number);

        // Calculate total minutes from start of day
        let totalMinutes =
          startHours * 60 + startMinutes + parseInt(values.duration);
        let endDay = day;
        let endMonth = month;
        let endYear = year;

        // Handle day rollover if meeting goes past midnight
        while (totalMinutes >= 1440) {
          // 1440 minutes = 24 hours
          totalMinutes -= 1440;
          endDay++;

          // Handle month rollover
          const daysInMonth = new Date(endYear, endMonth, 0).getDate();
          if (endDay > daysInMonth) {
            endDay = 1;
            endMonth++;

            // Handle year rollover
            if (endMonth > 12) {
              endMonth = 1;
              endYear++;
            }
          }
        }

        const endHours = Math.floor(totalMinutes / 60);
        const endMinutes = totalMinutes % 60;
        const endDateISO = `${endYear}-${String(endMonth).padStart(2, "0")}-${String(endDay).padStart(2, "0")}T${String(endHours).padStart(2, "0")}:${String(endMinutes).padStart(2, "0")}:00`;

        // Find the 'Call' task for the selected stage
        const callTask = meetingTasks
          .find((item) => item.stage.id === values.stage)
          ?.tasks.find((task) => task.label?.toLowerCase().includes("call"));

        // Get attendee emails for Microsoft meeting (TO field)
        const getAttendeeEmails = () => {
          const emails = [];

          // Add company member emails
          values.company_members.forEach((memberId) => {
            const member = companyMembersData.find((m) => m.id === memberId);
            if (member?.email) emails.push(member.email);
          });

          // Add moderator emails for both meeting types
          values.users.forEach((userId) => {
            const user = userData.find((u) => u.pk === userId);
            if (user?.email) emails.push(user.email);
          });

          // Add additional email invitees (these go to TO field)
          if (additionalEmails && additionalEmails.length > 0) {
            emails.push(...additionalEmails);
          }

          return [...new Set(emails)]; // Remove duplicates
        };

        // If Microsoft Teams meeting, create via Outlook Calendar API
        if (
          meetingType === "microsoft" &&
          outlookConnected &&
          showInterviewDetails
        ) {
          const attendeeList = getAttendeeEmails();

          // Default title: Just the company name for a clean professional look
          const defaultTitle = companyName
            ? `Meeting with ${companyName}`
            : "Meeting";

          const microsoftMeetingData = {
            subject: meetingTitle || defaultTitle,
            start: startDateISO,
            end: endDateISO,
            timezone: selectedTimezone,
            attendees: attendeeList,
            is_online_meeting: true,
            location: "Microsoft Teams",
            body: meetingDescription
              ? `<p>${meetingDescription.replace(/\n/g, "</p><p>")}</p><p>Please join using the Microsoft Teams link that will be included in this calendar invite.</p>`
              : `<p>You have been invited to a Microsoft Teams meeting.</p><p><strong>Stage:</strong> ${currentStageName || "N/A"}</p><p>Please join using the Microsoft Teams link that will be included in this calendar invite.</p>`,
            body_type: "HTML",
            save_to_db: true,
            company_id: companyId,
            stage_id: values.stage,
            task_id: callTask?.id || "call",
          };

          // Add CC emails if any
          if (ccEmails && ccEmails.length > 0) {
            microsoftMeetingData.cc = ccEmails;
          }

          const createdEvent =
            await createMicrosoftMeeting(microsoftMeetingData);

          // Extract Teams meeting URL and ID from response
          const teamsUrl =
            createdEvent?.onlineMeetingUrl ||
            createdEvent?.onlineMeeting?.joinUrl ||
            createdEvent?.webLink ||
            null;
          const meetingId =
            createdEvent?.id || createdEvent?.onlineMeeting?.id || null;

          console.log("Created Event Response:", createdEvent);
          console.log("Teams URL:", teamsUrl);
          console.log("Meeting ID:", meetingId);

          // Also create meeting record in our system to show card in stage
          const meetingRecordData = {
            company_id: companyId,
            stage_id: values.stage,
            task_id: callTask?.id || "call",
            attendees: values.company_members,
            moderators: values.users,
            start_date: startDateISO,
            end_date: endDateISO,
            meeting_timezone: selectedTimezone,
            microsoft_teams_url: teamsUrl,
            microsoft_meeting_id: meetingId,
            is_microsoft_teams: true,
            status: "CONFIRMED",
            isGatewayMeetScheduled: false,
            additional_invitees: additionalEmails,
          };

          // await axios.post(`${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/meeting/`, meetingRecordData, {
          //   headers: {
          //     Authorization: `Token ${localStorage.getItem('authToken')}`,
          //     'Content-Type': 'application/json'
          //   }
          // });

          // Show success with Teams link if available
          if (teamsUrl) {
            showToast(
              `Microsoft Teams meeting scheduled! Invites sent with Teams link.`,
            );
          } else {
            showToast(
              "Microsoft Teams meeting scheduled successfully! Invites sent to attendees.",
            );
          }
        } else if (
          meetingType === "microsoft" &&
          outlookConnected &&
          !showInterviewDetails
        ) {
          // Microsoft selected but checkbox not checked - just show info
          showToast(
            "Please check the checkbox to send calendar invite with Teams link.",
          );
          setLoading(false);
          return;
        } else {
          // Gateway Meet - use existing API
          const data = {
            company_id: companyId,
            stage_id: values.stage,
            task_id: callTask?.id || "call",
            attendees: values.company_members,
            moderators: values.users,
            start_date: startDateISO,
            end_date: endDateISO,
            meeting_timezone: selectedTimezone,
            status: "CONFIRMED",
            isGatewayMeetScheduled: showInterviewDetails,
            additional_invitees: additionalEmails,
          };

          await axios.post(
            `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/meeting/`,
            data,
            {
              headers: {
                Authorization: `Token ${localStorage.getItem("authToken")}`,
                "Content-Type": "application/json",
              },
            },
          );

          showToast("Gateway Meet scheduled successfully!");
        }
        setAdditionalEmails([]);
        setEmailInput("");
        setCcEmails([]);
        setCcInput("");
        setMeetingTitle("");
        setMeetingDescription("");
        formik.resetForm();
        onSuccess?.();
      } catch (error) {
        showErrorToast("Error scheduling meeting");
      } finally {
        setLoading(false);
      }
    },
  });

  const meetingTasks = journeyItemData
    .filter((item) => item.tasks?.some((task) => task.type === "MEETING"))
    .map((item) => ({
      ...item,
      tasks: item.tasks.filter((task) => task.type === "MEETING"),
    }));

  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Stage Display (Read-only) - Compact */}
          <Box
            sx={{
              p: 1.5,
              backgroundColor: "#f8f9fa",
              borderRadius: 1,
              border: "1px solid #e0e0e0",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.7rem" }}
            >
              STAGE:
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: "#1e293b" }}
            >
              {currentStageName || "No stage selected"}
            </Typography>
          </Box>

          {/* Meeting Type Toggle */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Meeting Platform
            </Typography>
            <ToggleButtonGroup
              value={meetingType}
              exclusive
              onChange={(e, newValue) => {
                if (newValue) {
                  setMeetingType(newValue);
                  // Auto-check showInterviewDetails when selecting a meeting type
                  setShowInterviewDetails(true);
                }
              }}
              fullWidth
              sx={{ mb: 1 }}
            >
              <ToggleButton
                value="microsoft"
                // disabled={!outlookConnected && !checkingOutlook}
                sx={{
                  py: 1.5,
                  "&.Mui-selected": {
                    backgroundColor: "#e3f2fd",
                    color: "#1565c0",
                    "&:hover": { backgroundColor: "#bbdefb" },
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <VideoCallIcon fontSize="small" sx={{ color: "#5059c9" }} />
                  Microsoft Teams
                </Box>
              </ToggleButton>
              <ToggleButton
                value="gateway"
                sx={{
                  py: 1.5,
                  "&.Mui-selected": {
                    backgroundColor: "#e8f5e9",
                    color: "#2e7d32",
                    "&:hover": { backgroundColor: "#c8e6c9" },
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <VideoCallIcon fontSize="small" />
                  Gateway Meet
                </Box>
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Outlook connection status */}
            {/* {!outlookConnected && !checkingOutlook && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Connect your Outlook account in the sidebar to use Microsoft Teams meetings.
                </Alert>
              )}
              {checkingOutlook && (
                <Typography variant="caption" color="text.secondary">
                  Checking Outlook connection...
                </Typography>
              )} */}
          </Box>

          {meetingType === "microsoft" && !outlookConnected ? (
            <>
              {checkingOutlook ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  align="center"
                >
                  Checking Outlook connection...
                </Typography>
              ) : (
                <Box sx={{ mt: 3, textAlign: "center" }}>
                  <Alert severity="error" sx={{ mb: 2, textAlign: "left" }}>
                    {outlookExpired && outlookExpiredEmails.length > 0 ? (
                      <>
                        <strong>Outlook connection expired!</strong>
                        <br />
                        Your Outlook connection (
                        {outlookExpiredEmails.join(", ")}) has expired or been
                        reset. Please reconnect your account to schedule
                        Microsoft Teams meetings and sync recordings.
                      </>
                    ) : (
                      <>
                        <strong>Outlook not connected</strong>
                        <br />
                        Please connect your Outlook account to schedule
                        Microsoft Teams meetings. Without an active connection,
                        meetings cannot be created and recordings will not sync.
                      </>
                    )}
                  </Alert>
                  <Button
                    variant="contained"
                    size="large"
                    href="/outlook-email"
                    color="error"
                    sx={{ minWidth: "250px" }}
                  >
                    {outlookExpired
                      ? "Reconnect Outlook Account"
                      : "Connect Outlook Account"}
                  </Button>
                </Box>
              )}
            </>
          ) : (
            <>
              {/* Sending Email Selector for Microsoft Teams */}
              {meetingType === "microsoft" && outlookConnections.length > 0 && (
                <FormControl fullWidth size="small">
                  <InputLabel>Sending From</InputLabel>
                  <Select
                    value={selectedConnectionId || ""}
                    onChange={(e) => setSelectedConnectionId(e.target.value)}
                    label="Sending From"
                  >
                    {outlookConnections.map((conn) => (
                      <MenuItem key={conn.id} value={String(conn.id)}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            width: "100%",
                          }}
                        >
                          <Typography variant="body2">{conn.email}</Typography>
                          {conn.is_default && (
                            <Chip
                              label="Default"
                              size="small"
                              color="primary"
                              sx={{ height: 20, fontSize: "0.65rem" }}
                            />
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Title and Description for Microsoft Teams */}
              {meetingType === "microsoft" && (
                <>
                  <TextField
                    fullWidth
                    label="Meeting Title"
                    value={
                      meetingTitle ||
                      `${currentStageName || "Meeting"}${companyName ? ` - ${companyName}` : ""}`
                    }
                    onChange={(e) => setMeetingTitle(e.target.value)}
                    size="small"
                  />
                  <TextField
                    fullWidth
                    label="Meeting Description"
                    placeholder="Enter meeting description (email body)"
                    value={meetingDescription}
                    onChange={(e) => setMeetingDescription(e.target.value)}
                    multiline
                    rows={3}
                    size="small"
                  />
                </>
              )}

              {/* Company Members */}
              <Autocomplete
                multiple
                options={companyMembersData.sort((a, b) => {
                  const aFirstName = a?.first_name || "";
                  const bFirstName = b?.first_name || "";
                  const aLastName = a?.last_name || "";
                  const bLastName = b?.last_name || "";

                  return aFirstName === bFirstName
                    ? aLastName.localeCompare(bLastName)
                    : aFirstName.localeCompare(bFirstName);
                })}
                getOptionLabel={(member) =>
                  `${member?.first_name || ""} ${member?.last_name || ""}`
                }
                value={companyMembersData.filter((member) =>
                  formik.values?.company_members?.includes(member?.id),
                )}
                onChange={(event, value) =>
                  formik.setFieldValue(
                    "company_members",
                    value?.map((obj) => obj?.id),
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Company Members (Attendees)"
                    error={
                      formik.touched.company_members &&
                      Boolean(formik.errors.company_members)
                    }
                    helperText={
                      formik.touched.company_members &&
                      formik.errors.company_members
                    }
                  />
                )}
              />

              {/* Additional Email Invitees */}
              <Box>
                <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                  <TextField
                    fullWidth
                    label="Add Email Invitees"
                    placeholder="Enter email address"
                    value={emailInput}
                    onChange={(e) => {
                      setEmailInput(e.target.value);
                      setEmailError("");
                    }}
                    onKeyPress={handleKeyPress}
                    size="small"
                    error={Boolean(emailError)}
                    helperText={emailError}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleAddEmail}
                    startIcon={<AddIcon />}
                    sx={{ minWidth: "100px" }}
                  >
                    Add
                  </Button>
                </Box>
                {additionalEmails.length > 0 && (
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}
                  >
                    {additionalEmails.map((email, index) => (
                      <Chip
                        key={index}
                        label={email}
                        onDelete={() => handleDeleteEmail(email)}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                )}
              </Box>

              {/* Users/Moderators - For both meeting types */}
              <Autocomplete
                multiple
                options={userData.sort((a, b) => {
                  const aFirstName = a?.first_name || "";
                  const bFirstName = b?.first_name || "";
                  const aLastName = a?.last_name || "";
                  const bLastName = b?.last_name || "";

                  return aFirstName === bFirstName
                    ? aLastName.localeCompare(bLastName)
                    : aFirstName.localeCompare(bFirstName);
                })}
                getOptionLabel={(user) =>
                  `${user?.first_name || ""} ${user?.last_name || ""}`
                }
                onChange={(event, value) =>
                  formik.setFieldValue(
                    "users",
                    value?.map((obj) => obj?.pk),
                  )
                }
                value={userData.filter((user) =>
                  formik.values?.users?.includes(user?.pk),
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Moderators"
                    error={formik.touched.users && Boolean(formik.errors.users)}
                    helperText={formik.touched.users && formik.errors.users}
                  />
                )}
              />

              {/* CC Emails - Only for Microsoft Teams */}
              {meetingType === "microsoft" && (
                <Box>
                  <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                    <TextField
                      fullWidth
                      label="CC Email Addresses"
                      placeholder="Enter CC email address"
                      value={ccInput}
                      onChange={(e) => {
                        setCcInput(e.target.value);
                        setCcError("");
                      }}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddCcEmail();
                        }
                      }}
                      size="small"
                      error={Boolean(ccError)}
                      helperText={ccError}
                    />
                    <Button
                      variant="outlined"
                      onClick={handleAddCcEmail}
                      startIcon={<AddIcon />}
                      sx={{ minWidth: "100px" }}
                    >
                      Add
                    </Button>
                  </Box>
                  {ccEmails.length > 0 && (
                    <Box
                      sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}
                    >
                      {ccEmails.map((email, index) => (
                        <Chip
                          key={index}
                          label={email}
                          onDelete={() => handleDeleteCcEmail(email)}
                          color="secondary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              {/* Timezone Selection - FIRST - Searchable (Dynamic) */}
              <Box>
                <Autocomplete
                  options={allTimezones || []}
                  value={
                    allTimezones?.find((tz) => tz.value === selectedTimezone) ||
                    null
                  }
                  onChange={(event, newValue) => {
                    setSelectedTimezone(newValue?.value || "Asia/Kolkata");
                    setUserSelectedTimezone(true); // Mark as user-selected to prevent auto-override
                  }}
                  getOptionLabel={(option) => option?.label || ""}
                  isOptionEqualToValue={(option, value) =>
                    option?.value === value?.value
                  }
                  filterOptions={(options, { inputValue }) => {
                    if (!inputValue) return options;

                    const searchLower = inputValue.toLowerCase().trim();

                    // Filter and sort results
                    const filtered = options.filter((option) =>
                      option?.searchText?.includes(searchLower),
                    );

                    // Sort to prioritize country name matches over timezone names
                    return filtered.sort((a, b) => {
                      const aCountries = (a.countries || "").toLowerCase();
                      const bCountries = (b.countries || "").toLowerCase();
                      const aName = a.value.toLowerCase();
                      const bName = b.value.toLowerCase();

                      // Exact match in country names gets highest priority
                      const aExactCountry = aCountries
                        .split(",")
                        .map((c) => c.trim())
                        .includes(searchLower);
                      const bExactCountry = bCountries
                        .split(",")
                        .map((c) => c.trim())
                        .includes(searchLower);

                      if (aExactCountry && !bExactCountry) return -1;
                      if (!aExactCountry && bExactCountry) return 1;

                      // Country name starts with search term
                      const aCountryStarts = aCountries
                        .split(",")
                        .some((c) => c.trim().startsWith(searchLower));
                      const bCountryStarts = bCountries
                        .split(",")
                        .some((c) => c.trim().startsWith(searchLower));

                      if (aCountryStarts && !bCountryStarts) return -1;
                      if (!aCountryStarts && bCountryStarts) return 1;

                      // Then timezone name matches
                      const aStarts = aName.startsWith(searchLower);
                      const bStarts = bName.startsWith(searchLower);

                      if (aStarts && !bStarts) return -1;
                      if (!aStarts && bStarts) return 1;

                      return 0;
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Timezone"
                      placeholder="Search timezone..."
                    />
                  )}
                />

                {/* Time Difference Indicator */}
                {selectedTimezone && (
                  <Box
                    sx={{
                      mt: 1,
                      p: 1.5,
                      backgroundColor: "#f0f9ff",
                      borderRadius: 1,
                      border: "1px solid #bae6fd",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#0369a1",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                      }}
                    >
                      ⏰ {getTimeDifference(selectedTimezone)}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Meeting Date & Time - Same Row */}
              <Box sx={{ display: "flex", gap: 2 }}>
                {/* Meeting Date */}
                <TextField
                  fullWidth
                  label="Meeting Date"
                  type="date"
                  value={formik.values.date}
                  onChange={formik.handleChange}
                  name="date"
                  inputProps={{ min: getMinStartDate() }}
                  InputLabelProps={{ shrink: true }}
                  error={formik.touched.date && Boolean(formik.errors.date)}
                  helperText={formik.touched.date && formik.errors.date}
                />

                {/* Meeting Time - Searchable Dropdown */}
                <Autocomplete
                  fullWidth
                  options={[
                    "00:00",
                    "00:30",
                    "01:00",
                    "01:30",
                    "02:00",
                    "02:30",
                    "03:00",
                    "03:30",
                    "04:00",
                    "04:30",
                    "05:00",
                    "05:30",
                    "06:00",
                    "06:30",
                    "07:00",
                    "07:30",
                    "08:00",
                    "08:30",
                    "09:00",
                    "09:30",
                    "10:00",
                    "10:30",
                    "11:00",
                    "11:30",
                    "12:00",
                    "12:30",
                    "13:00",
                    "13:30",
                    "14:00",
                    "14:30",
                    "15:00",
                    "15:30",
                    "16:00",
                    "16:30",
                    "17:00",
                    "17:30",
                    "18:00",
                    "18:30",
                    "19:00",
                    "19:30",
                    "20:00",
                    "20:30",
                    "21:00",
                    "21:30",
                    "22:00",
                    "22:30",
                    "23:00",
                    "23:30",
                  ]}
                  value={formik.values.time}
                  onChange={(event, value) =>
                    formik.setFieldValue("time", value || "09:00")
                  }
                  freeSolo
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Meeting Time"
                      error={formik.touched.time && Boolean(formik.errors.time)}
                      helperText={formik.touched.time && formik.errors.time}
                    />
                  )}
                />
              </Box>

              {/* Duration Quick Select - Always Visible */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Duration
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  {[
                    { value: 30, label: "30 min" },
                    { value: 60, label: "1 hr" },
                    { value: 90, label: "1.5 hrs" },
                    { value: 120, label: "2 hrs" },
                    { value: 180, label: "3 hrs" },
                    { value: 240, label: "4 hrs" },
                  ].map((option) => (
                    <Button
                      key={option.value}
                      variant={
                        formik.values.duration === option.value && !customHours
                          ? "contained"
                          : "outlined"
                      }
                      onClick={() => {
                        formik.setFieldValue("duration", option.value);
                        setCustomHours("");
                      }}
                      size="small"
                      sx={{ minWidth: "80px" }}
                    >
                      {option.label}
                    </Button>
                  ))}
                  <TextField
                    label="Custom (h.mm)"
                    type="text"
                    value={customHours}
                    onChange={(e) => {
                      const input = e.target.value;
                      setCustomHours(input);
                      if (input) {
                        // Parse as hours.minutes format (e.g., 1.20 = 1 hour 20 minutes)
                        const parts = input.split(".");
                        const hours = parseInt(parts[0]) || 0;
                        const minutes = parts[1]
                          ? parseInt(parts[1].padEnd(2, "0").substring(0, 2))
                          : 0;
                        const totalMinutes = hours * 60 + minutes;
                        formik.setFieldValue("duration", totalMinutes);
                      }
                    }}
                    size="small"
                    sx={{ width: "160px" }}
                    placeholder="e.g., 1.30"
                  />
                  {customHours && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 1, whiteSpace: "nowrap" }}
                    >
                      {(() => {
                        const parts = customHours.split(".");
                        const hours = parseInt(parts[0]) || 0;
                        const minutes = parts[1]
                          ? parseInt(parts[1].padEnd(2, "0").substring(0, 2))
                          : 0;
                        if (hours === 0) return `${minutes} mins`;
                        if (minutes === 0)
                          return `${hours} hour${hours > 1 ? "s" : ""}`;
                        return `${hours}h ${minutes}m`;
                      })()}
                    </Typography>
                  )}
                </Box>
                {formik.touched.duration && formik.errors.duration && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    {formik.errors.duration}
                  </Typography>
                )}
              </Box>

              {/* Meeting invite option - label changes based on meeting type */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showInterviewDetails}
                    onChange={(e) => {
                      setShowInterviewDetails(e.target.checked);
                      formik.setFieldValue(
                        "showInterviewDetails",
                        e.target.checked,
                      );
                    }}
                  />
                }
                label={
                  meetingType === "microsoft"
                    ? "Send calendar invite and create Teams meeting link"
                    : "Send calendar invite and create Gateway Meet link"
                }
              />
            </>
          )}
        </Box>

        {!(meetingType === "microsoft" && !outlookConnected) && (
          <Box sx={{ mt: 3, display: "flex", gap: 1 }}>
            <Button
              onClick={onClose}
              disabled={loading}
              fullWidth
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              fullWidth
            >
              {loading ? "Scheduling..." : "Schedule Meeting"}
            </Button>
          </Box>
        )}
      </form>
    </>
  );
}
